import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell, inputCls, btnCls } from "@/components/AuthShell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("The two passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset your password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token)
    return (
      <AuthShell title="Reset password">
        <p data-testid="reset-missing-token" className="text-sm text-slate-400">This link is missing its code. <Link to="/forgot-password" className="text-orange-400">Request a new reset link</Link>.</p>
      </AuthShell>
    );

  return (
    <AuthShell title="Set a new password" subtitle="Choose a password with at least 6 characters.">
      {done ? (
        <div data-testid="reset-success" className="space-y-4">
          <p className="flex gap-3 bg-emerald-950/40 border border-emerald-900/50 rounded-xl p-4 text-sm text-emerald-300"><CheckCircle2 className="h-5 w-5 shrink-0" /> Password updated. You can log in with your new password.</p>
          <Link to="/login" data-testid="reset-go-login" className={btnCls}>Go to log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">New password</label>
            <input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Confirm new password</label>
            <input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
          </div>
          {error && (
            <p data-testid="reset-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
              {error} {error.includes("expired") && <Link to="/forgot-password" className="underline">Request a new link</Link>}
            </p>
          )}
          <button data-testid="reset-submit-button" type="submit" disabled={loading} className={btnCls}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Save new password
          </button>
        </form>
      )}
    </AuthShell>
  );
}
